import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import axios from 'axios'
import CustomPrimeTable, { DEFAULT_PAGE_SAZE } from '../../common/PrimeTable/CustomPrimeTable'
import Loader from '../Loader'
import { createColumns } from '../SMS/utils/columnDefinitions'

const DEFAULT_PAGINATION = { limit: DEFAULT_PAGE_SAZE, first: 0 }

const SMSTableModal = ({ userId, direction, dateFilter }) => {
  const { user } = useSelector((state) => ({
    user: state.auth.user,
  }))

  const [smsData, setSmsData] = useState([])
  const [smsQuery, setSmsQuery] = useState({ totalRecords: 0, limit: DEFAULT_PAGE_SAZE, offset: 0 })
  const [isLoading, setIsLoading] = useState(false)

  const [options, setOptions] = useState({
    pagination: DEFAULT_PAGINATION,
    sort: { creation_date: -1 },
    tableFilter: {},
  })

  useEffect(() => {
    fetchSMSData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.pagination, options.sort, userId, direction, dateFilter])

  const fetchSMSData = async () => {
    try {
      setIsLoading(true)

      // Build table filter
      const tableFilter = { ...options.tableFilter }

      // Add user filter
      if (userId) {
        tableFilter['users.id'] = [userId]
      }

      // Add direction filter if specified
      if (direction) {
        tableFilter.direction = direction
      }

      // Add date range filter if specified
      if (dateFilter && dateFilter.length === 2) {
        tableFilter.creation_date = {
          start: dateFilter[0],
          end: dateFilter[1],
        }
      }

      const response = await axios.get('/ringcentral-sms/table', {
        params: {
          pagination: JSON.stringify(options.pagination),
          sort: JSON.stringify(options.sort),
          tableFilter: JSON.stringify(tableFilter),
        },
      })

      const { data, query } = response.data

      // Transform data to match expected format
      const transformedData = data.map((sms) => ({
        ...sms,
        userName: sms.user_name || sms.userName,
        contactName: sms.contact_name || sms.contactName,
        fromNumber: sms.from_number || sms.fromNumber,
        toNumber: sms.to_number || sms.toNumber,
        messageStatus: sms.message_status || sms.messageStatus,
        creationDate: sms.creation_date || sms.creationDate,
        contact_id: sms.contact_id,
        contactCategory: sms.contact_category || sms.contactCategory,
      }))

      setSmsData(transformedData)
      setSmsQuery(query)
    } catch (error) {
      console.error('Error fetching SMS data:', error)
      setSmsData([])
      setSmsQuery({ totalRecords: 0, limit: DEFAULT_PAGE_SAZE, offset: 0 })
    } finally {
      setIsLoading(false)
    }
  }

  const onPageChange = (event) => {
    setOptions((prev) => ({
      ...prev,
      pagination: {
        limit: event.rows,
        first: event.first,
      },
    }))
  }

  const onSortChange = (event) => {
    let sort = {}
    if (event.multiSortMeta) {
      event.multiSortMeta.forEach((row) => {
        sort[row.field] = row.order
      })
    }
    setOptions((prev) => ({
      ...prev,
      sort,
      pagination: DEFAULT_PAGINATION,
    }))
  }

  const columns = createColumns({ user })

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader custom />
        </div>
      )}

      <CustomPrimeTable
        data={smsData}
        columns={columns}
        addHidden
        findHidden
        onSort={onSortChange}
        onPage={onPageChange}
        query={smsQuery}
        showActions={false}
      />
    </div>
  )
}

SMSTableModal.propTypes = {
  userId: PropTypes.number,
  direction: PropTypes.string,
  dateFilter: PropTypes.array,
}

SMSTableModal.defaultProps = {
  userId: null,
  direction: null,
  dateFilter: null,
}

export default SMSTableModal
